import React, { useMemo, useCallback } from 'react';
import ParameterTreeNode from './ParameterTreeNode';
import ParameterTableList from './ParameterTableList'; // Import the new component
import { EventSchema, UnifiedSchema, Parameter, FilterOptions } from '../../types/schema';
import { debounce } from '../../utils/schemaUtils';

export type ParameterViewMode = 'tree' | 'table'; // Define a new type for view mode

interface ParameterListContainerProps {
  unifiedSchema: UnifiedSchema | null;
  selectedEvent: EventSchema | null;
  isEditable: boolean;
  filteredGlobalTreeNodes: Parameter[];
  filteredEventSpecificTreeNodes: Parameter[];
  filterOptions: FilterOptions;
  globalAllNodesMap: Map<string, Parameter>;
  eventSpecificAllNodesMap: Map<string, Parameter>;
  toggleTreeNode: (path: string, type: 'global' | 'eventSpecific') => void;
  handleParameterChange: (paramId: string, updates: Partial<Parameter>) => void;
  handleDeleteParameter: (paramId: string, deleteFromAllEvents?: boolean) => void;
  handleAddParameter: (eventId: string, parentParam: Parameter | null, targetScope: 'global' | 'eventSpecific') => void;
  paramToEditId: string | null;
  clearParamToEditId: () => void;
  activeEditingParamId: string | null;
  setActiveEditingParamId: React.Dispatch<React.SetStateAction<string | null>>;
  handleParameterAssociationChange: (propertyFullPath: string, eventId: string, isAssociated: boolean) => void;
  allEvents: EventSchema[];
  viewMode: ParameterViewMode; // Add viewMode prop
}

const ParameterListContainer: React.FC<ParameterListContainerProps> = ({
  unifiedSchema,
  selectedEvent,
  isEditable,
  filteredGlobalTreeNodes,
  filteredEventSpecificTreeNodes,
  filterOptions,
  globalAllNodesMap,
  eventSpecificAllNodesMap,
  toggleTreeNode,
  handleParameterChange,
  handleDeleteParameter,
  handleAddParameter,
  paramToEditId,
  clearParamToEditId,
  activeEditingParamId,
  setActiveEditingParamId,
  handleParameterAssociationChange,
  allEvents,
  viewMode, // Destructure viewMode
}) => {
  // Debounced version for ParameterTreeNode to use with increased delay
  const debouncedHandleParameterChange = React.useMemo(
    () => debounce(handleParameterChange, 300),
    [handleParameterChange]
  );

  const handleAddSubParameter = useCallback((parentParam: Parameter) => {
    // Determine eventId based on selectedEvent context.
    // ParameterTreeNode's onAddParameter only fires if selectedEvent exists.
    // So, we align this behavior.
    const eventIdForNewParam = selectedEvent ? selectedEvent.id : '';
    handleAddParameter(eventIdForNewParam, parentParam, parentParam.scope);
  }, [handleAddParameter, selectedEvent]);

  const flattenParameters = useCallback((nodes: Parameter[]): Parameter[] => {
    let flatList: Parameter[] = [];
    nodes.forEach(node => {
      flatList.push(node);
      if (node.children && node.children.length > 0) {
        flatList = flatList.concat(flattenParameters(node.children));
      }
    });
    return flatList;
  }, []);

  const allFilteredParameters = useMemo(() => {
    let combined: Parameter[] = [];
    if (filterOptions.showGlobal) {
      combined = combined.concat(flattenParameters(filteredGlobalTreeNodes));
    }
    if (filterOptions.showEventSpecific) {
      combined = combined.concat(flattenParameters(filteredEventSpecificTreeNodes));
    }
    return combined;
  }, [filteredGlobalTreeNodes, filteredEventSpecificTreeNodes, filterOptions.showGlobal, filterOptions.showEventSpecific, flattenParameters]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:border dark:border-gray-700 flex-shrink-0 overflow-hidden mb-6 pl-4 mx-[2px]">
      <div className={viewMode === 'tree' ? 'py-4' : ''}>
        {viewMode === 'tree' && (filteredGlobalTreeNodes.length > 0 || filteredEventSpecificTreeNodes.length > 0) ? (
          <>
            {/* Global Properties */}
            {filterOptions.showGlobal && filteredGlobalTreeNodes.length > 0 && (
              <>
                {unifiedSchema && unifiedSchema.events && filteredGlobalTreeNodes.map((node: Parameter) => (
                  <ParameterTreeNode
                    key={node.id}
                    param={node}
                    scopeType="global"
                    allNodesMap={globalAllNodesMap}
                    toggleTreeNode={toggleTreeNode}
                    selectedEvent={selectedEvent}
                    handleParameterChange={debouncedHandleParameterChange}
                    handleDeleteParameter={handleDeleteParameter}
                    handleAddParameter={handleAddParameter}
                    isEditable={isEditable}
                    paramToEditId={paramToEditId}
                    clearParamToEditId={clearParamToEditId}
                    activeEditingParamId={activeEditingParamId}
                    setActiveEditingParamId={setActiveEditingParamId}
                    allEvents={allEvents}
                    handleParameterAssociationChange={handleParameterAssociationChange}
                    viewMode={viewMode}
                  />
                ))}
              </>
            )}
            {/* Event-Specific Properties */}
            {filterOptions.showEventSpecific && filteredEventSpecificTreeNodes.length > 0 && (
              <>
                {unifiedSchema && unifiedSchema.events && filteredEventSpecificTreeNodes.map((node: Parameter) => (
                  <ParameterTreeNode
                    key={node.id}
                    param={node}
                    scopeType="eventSpecific"
                    allNodesMap={eventSpecificAllNodesMap}
                    toggleTreeNode={toggleTreeNode}
                    selectedEvent={selectedEvent}
                    handleParameterChange={debouncedHandleParameterChange}
                    handleDeleteParameter={handleDeleteParameter}
                    handleAddParameter={handleAddParameter}
                    isEditable={isEditable}
                    paramToEditId={paramToEditId}
                    clearParamToEditId={clearParamToEditId}
                    activeEditingParamId={activeEditingParamId}
                    setActiveEditingParamId={setActiveEditingParamId}
                    allEvents={allEvents}
                    handleParameterAssociationChange={handleParameterAssociationChange}
                    viewMode={viewMode}
                  />
                ))}
              </>
            )}
          </>
        ) : viewMode === 'table' ? ( // Removed length check here, ParameterTableList will handle empty state
          <ParameterTableList
            parameters={allFilteredParameters}
            allEvents={allEvents}
            isEditable={isEditable}
            handleParameterChange={handleParameterChange} // Pass non-debounced handler
            handleDeleteParameter={handleDeleteParameter}
            onAddSubParameter={handleAddSubParameter} // Pass the new handler
            paramToEditId={paramToEditId}
            clearParamToEditId={clearParamToEditId}
            activeEditingParamId={activeEditingParamId}
            setActiveEditingParamId={setActiveEditingParamId}
            handleParameterAssociationChange={handleParameterAssociationChange}
          />
        ) : (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            {!filterOptions.showGlobal && !filterOptions.showEventSpecific 
              ? "No property types are selected in filters. Please adjust your filters to see properties."
              : "No properties match your current filters."}
          </p>
        )}
      </div>
    </div>
  );
};

export default ParameterListContainer;
